/**
 * Kredafi — отдельная оболочка того же приложения.
 *
 * На kredafi.webtransfer.app человек видит новый дизайн (кошелёк).
 * API, сессии и все экраны те же, что на webtransfer.app.
 * Telegram Mini App эту ветку не использует: там оболочка не меняется.
 */

import { isTelegramMiniApp } from "./platform";
import { getPathLocale } from "./ogPathLocale";
import { KREDAFI_ICON_CACHE } from "./iconCacheBuster";

export const KREDAFI_HOSTS = new Set([
  "kredafi.webtransfer.app",
  "www.kredafi.webtransfer.app",
  "kredafi.com",
  "www.kredafi.com",
]);

/**
 * /ru → русский интерфейс, URL в адресной строке сжимается до /.
 * Краулеры Telegram читают build/ru/index.html до этого скрипта.
 */
export function bootLocaleFromPath(): void {
  if (typeof window === "undefined" || isTelegramMiniApp) {
    return;
  }
  const code = getPathLocale();
  if (!code) {
    return;
  }
  const path = window.location.pathname;
  try {
    localStorage.setItem("language", code);
    localStorage.setItem("kredafi-lang", code);
  } catch {
    /* ignore */
  }
  const rest = path.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  window.history.replaceState(
    null,
    "",
    rest + window.location.search + window.location.hash
  );
}

export { getPathLocale, OG_PATH_LOCALES } from "./ogPathLocale";

/**
 * Пока nginx на origin не знает Host kredafi.webtransfer.app (нужен root),
 * оболочка открывается по https://webtransfer.app/kredafi — тот же бандл,
 * сессия только в этой вкладке, обычный webtransfer.app не меняется.
 */
export function bootKredafiFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (isTelegramMiniApp) {
    return;
  }
  const path = window.location.pathname;
  if (path !== "/kredafi" && !path.startsWith("/kredafi/")) {
    return;
  }
  try {
    sessionStorage.setItem("wt_kredafi_preview", "1");
  } catch {
    /* ignore */
  }
  const rest =
    path === "/kredafi" || path === "/kredafi/"
      ? "/"
      : path.replace(/^\/kredafi/, "") || "/";
  window.history.replaceState(
    null,
    "",
    rest + window.location.search + window.location.hash
  );
}

export function isKredafiHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const host = window.location.hostname.toLowerCase();
  if (
    KREDAFI_HOSTS.has(host) ||
    host.endsWith(".kredafi.webtransfer.app") ||
    host.endsWith(".kredafi.com")
  ) {
    return true;
  }
  try {
    if (sessionStorage.getItem("wt_kredafi_preview") === "1") {
      return true;
    }
    // Локальный предпросмотр разработчика: localStorage.wt_brand=kredafi
    return localStorage.getItem("wt_brand") === "kredafi";
  } catch {
    return false;
  }
}

/** Новая оболочка: только веб/PWA на хосте Kredafi, мини-апп не трогаем. */
export function isKredafiShell(): boolean {
  return !isTelegramMiniApp && isKredafiHost();
}

export function applyKredafiDocumentMeta(): void {
  if (typeof document === "undefined" || !isKredafiShell()) {
    return;
  }
  document.title = "KredaFi";
  document.documentElement.classList.add("kredafi");

  // Гарантируем корректный viewport для мобильных экранов
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement("meta");
    viewport.setAttribute("name", "viewport");
    document.head.appendChild(viewport);
  }
  viewport.setAttribute(
    "content",
    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
  );

  let theme = document.querySelector('meta[name="theme-color"]');
  if (!theme) {
    theme = document.createElement("meta");
    theme.setAttribute("name", "theme-color");
    document.head.appendChild(theme);
  }
  theme.setAttribute("content", "#00B6B4");
  const appTitle = document.querySelector(
    'meta[name="apple-mobile-web-app-title"]'
  );
  if (appTitle) {
    appTitle.setAttribute("content", "KredaFi");
  }
  const appName = document.querySelector('meta[name="application-name"]');
  if (appName) {
    appName.setAttribute("content", "KredaFi");
  }

  // Динамически выставляем правильный фавикон и иконку PWA для KredaFi
  const v = KREDAFI_ICON_CACHE;
  const iconPng = `/assets/kredafi-icon-192.png?v=${v}`;
  const maskSvg = `/assets/kredafi-mask.svg?v=${v}`;

  document.querySelectorAll('link[rel="icon"][type="image/svg+xml"]').forEach((el) => {
    el.parentNode?.removeChild(el);
  });

  document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((el) => {
    const sizes = el.getAttribute("sizes") || "";
    if (sizes.includes("16x16")) {
      el.setAttribute("href", `/favicon-16x16.png?v=${v}`);
    } else {
      el.setAttribute("href", `/favicon-32x32.png?v=${v}`);
    }
  });

  // Обновляем apple-touch-icon для iOS Safari
  document.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => {
    el.setAttribute("href", iconPng);
  });

  // Safari pinned tab / sidebar mask-icon
  let maskLink = document.querySelector('link[rel="mask-icon"]');
  if (!maskLink) {
    maskLink = document.createElement("link");
    maskLink.setAttribute("rel", "mask-icon");
    maskLink.setAttribute("color", "#00B6B4");
    document.head.appendChild(maskLink);
  }
  maskLink.setAttribute("href", maskSvg);
}
